import { DecimalPipe } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isNull } from '@igniteui/material-icons-extended';
import { isEmpty } from 'rxjs';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { MessageService } from 'src/app/core/services/broadcast/message-service';
import { QueryMessageService } from 'src/app/core/services/broadcast/MessageDispatchingService';
import { DataService } from 'src/app/core/services/data/data.service';
import { InvoiceService } from 'src/app/core/services/data/invoice.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { AddedAdjustments } from 'src/app/shared/entities/AddedAdjustments';
import { AddInvoiceAdjustment } from 'src/app/shared/entities/AddInvoiceAdjustment';
import { AddInvoiceAdjustmentTax } from 'src/app/shared/entities/AddInvoiceAdjustmentTax';
import { AdjustmentsCompletedMI } from 'src/app/shared/entities/AdjustmentsCompletedMI';
import { AdjustTaxes } from 'src/app/shared/entities/AdjustTaxes';
import { InvoiceAdjustment } from 'src/app/shared/entities/InvoiceAdjustment';
import { InvoiceAdjustmentReasons } from 'src/app/shared/entities/InvoiceAdjustmentReasons';
import { InvoiceAdjustments } from 'src/app/shared/entities/InvoiceAdjustments';
import { PostMigrateAdjustment } from 'src/app/shared/entities/PostMigrateAdjustment';
import { QuickSearchResults } from 'src/app/shared/entities/QuickSearchResults';
import { TaxListMatchingInvoiceDate } from 'src/app/shared/entities/TaxListMatchingInvoiceDate';
import {
  IGBInvoiceAdjustmentDisplayData,
  IInvoiceAdjustmentAmount,
  IMiinvoiceAdjustmentResponse,
  IMiAdjustmentBillingLineResponse,
} from 'src/app/shared/interfaces';

@Component({
  selector: 'app-mi-invoice-adjustments',
  standalone: false,
  templateUrl: './mi-invoice-adjustments.component.html',
  styleUrl: './mi-invoice-adjustments.component.scss',
})
export class MiInvoiceAdjustmentsComponent {
  @Input()
  set invoiceNumber(value: string) {
    this.currentInvoiceNumber = value;
    const invoiceType = this.currentInvoiceNumber.substring(0, 2);
  }
  miInvoiceAdjustmentData: IMiinvoiceAdjustmentResponse;
  allinvoiceBillingLines: InvoiceAdjustments[] = [];
  currentInvoiceNumber: string = '';
  invoiceBillingLines: InvoiceAdjustments[] = [];
  completedAdjustmentsMI: AdjustmentsCompletedMI[] = [];
  addedAdjustments: AddedAdjustments[] = [];
  invoiceAdjustmentReasons: InvoiceAdjustmentReasons[] = [];
  matchingTaxListForInvoice: TaxListMatchingInvoiceDate[] = [];
  isCurrentInvoiceLineTaxable: boolean | undefined = undefined;
  netAmountMinimumLimit: number = 0;
  netAmountMaximumLimit: number = 0;
  grossAmountMinimumLimit: number = 0;
  grossAmountMaximumLimit: number = 0;
  currentInvoiceTaxRate: number = 0;
  isDev: boolean = false;
  invoiceBeingAdjustedDetails: InvoiceAdjustments = new InvoiceAdjustments();
  adjustedNetRevenue: number;
  showZeroBalanceInvoiceLines: boolean = false;
  showWaitMessage: boolean = false;
  netAmountReadOnly: boolean = true;
  grossAmountReadOnly: boolean = true;

  miInvoiceLineForm = new FormGroup({
    invoiceLineItem: new FormControl('', []),
    currentDate: new FormControl(this.getCurrentDate(), [Validators.required]),
    netAmount: new FormControl('', [
      Validators.required,
      Validators.min(this.netAmountMinimumLimit),
      Validators.max(this.netAmountMaximumLimit),
    ]),
    grossAmount: new FormControl('', [
      Validators.required,
      Validators.min(this.grossAmountMinimumLimit),
      Validators.max(this.grossAmountMaximumLimit),
    ]),
    adjustmentReason: new FormControl('', [Validators.required]),
    adjustmentComment: new FormControl('', [Validators.required, Validators.maxLength(512)]),
  });

  // SERVICES
  #_invoiceService = inject(InvoiceService);
  #_dataService = inject(DataService);
  #_appGeneralState = inject(AppGeneralStateService);
  #_decimalPipe = inject(DecimalPipe);
  #_messageService = inject(MessageService);
  #_messageDialogService = inject(MessageDialogService);
  #_queryMessagegService = inject(QueryMessageService);

  constructor() {
    this.isDev = false; //this.#_appGeneralState.isEnvironmentDev;
  }

  ngOnInit(): void {
    this.getMiInvoiceAdjustmentDetailsForInvoiceNumber(this.currentInvoiceNumber);
    this.getTaxListThatMatchInvoiceNumber(this.currentInvoiceNumber);
    this.getInvoiceAdjustmentReasonsForLotNumberFromDB(this.#_appGeneralState.currentLotNumber);
    this.getAdjustmentsCompletedForMI(this.currentInvoiceNumber);
  }

  onSubmitNewInvoiceAdjustment(): void {
    if (this.showWaitMessage == true) return;

    this.showWaitMessage = true;

    let invoiceAdjustmentDetails: AddInvoiceAdjustment = new AddInvoiceAdjustment();
    let invoiceAdjustmentTaxDetails: AddInvoiceAdjustmentTax[] = [];
    let billingDetailId: number = +this.miInvoiceLineForm.get('invoiceLineItem').value;

    this.invoiceBeingAdjustedDetails = this.getInvoiceAdjustmentDetailsForLineItem(billingDetailId);

    let grossAmount: number = +this.miInvoiceLineForm.get('grossAmount').value.replace(',', '');

    this.adjustedNetRevenue = +this.miInvoiceLineForm.get('netAmount').value.replace(',', '');

    invoiceAdjustmentDetails.AdjustmentAmount = this.adjustedNetRevenue;
    invoiceAdjustmentDetails.AdjustmentReasonId = +this.miInvoiceLineForm.get('adjustmentReason').value;
    invoiceAdjustmentDetails.BillingDetailId = billingDetailId;
    invoiceAdjustmentDetails.Comments = this.miInvoiceLineForm.get('adjustmentComment').value;
    invoiceAdjustmentDetails.GenerateBillingLineItemId = this.invoiceBeingAdjustedDetails.generateBillingLineItemId;
    invoiceAdjustmentDetails.InvoiceNumber = this.currentInvoiceNumber;
    invoiceAdjustmentDetails.IsLocked = false;
    invoiceAdjustmentDetails.LotNumber = this.#_appGeneralState.currentLotNumber;
    invoiceAdjustmentDetails.RevenueCode = ''; //this.getRevenueCodeFromRevenueName(this.invoiceBeingAdjustedDetails.description);
    invoiceAdjustmentDetails.UserName = this.#_appGeneralState.userName;
    invoiceAdjustmentDetails.ManualInvoiceId = this.invoiceBeingAdjustedDetails.manualInvoiceId;

    //todo refactor in separate method
    let ii: number = 0;
    let today = new Date();
    if (this.isCurrentInvoiceLineTaxable) {
      invoiceAdjustmentTaxDetails.push({
        AdjustmentPostingTaxId: 0,
        AdjustmentPostingId: 0,
        BillingDetailId: 0,
        TaxType: 'Total',
        AdjustedTax: grossAmount - this.adjustedNetRevenue,
        AdjustedTaxMoney: grossAmount - this.adjustedNetRevenue,
        DateEntered: today,
      });
    }

    this.postIUAdjustment(invoiceAdjustmentDetails, invoiceAdjustmentTaxDetails, this.isCurrentInvoiceLineTaxable);
  }

  getInvoiceAdjustmentDetailsForLineItem(billingDetailId: number): InvoiceAdjustments {
    let ii: number = 0;
    let adjustmentDetails: InvoiceAdjustments = new InvoiceAdjustments();
    for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
      if (billingDetailId === this.invoiceBillingLines[ii].billingDetailID) {
        adjustmentDetails = this.invoiceBillingLines[ii];
      }
    }
    return adjustmentDetails;
  }

  // This method gets called when the user selects an invoice line in the invoice line drop down list
  onInvoiceLineSelectionChange(): void {
    try {
      // Get the selected invoice line data
      const billingDetailId: number = +this.miInvoiceLineForm.get('invoiceLineItem').value;
      // Clear any results from a different line
      this.miInvoiceLineForm.patchValue({
        netAmount: '',
        grossAmount: '',
        adjustmentReason: '',
        adjustmentComment: '',
      });

      // If the primary key is blank/empty (designated by ''), it means the user chose the top dropdown item, which is the 'Select Invoice Line'
      if (billingDetailId == 0) {
        this.netAmountReadOnly = true;
        this.grossAmountReadOnly = true;
        // this path is blank/empty
        //This will reset the form elements
        this.isCurrentInvoiceLineTaxable = undefined;
        this.resetInvoiceBillingLineData();
        return;
      } else {
        // The user chose an invoice line.
        // Get a specific line
        let invoiceLine: InvoiceAdjustments = this.invoiceBillingLines.find(
          (line) => line.billingDetailID == billingDetailId,
        );

        // Check to see if the invoid line balance = 0
        const zeroCheckAmount: number = invoiceLine.totalAmount + invoiceLine.adjustment;
        if (invoiceLine.totalAmount + invoiceLine.adjustment == 0) {
          this.#_messageDialogService.snackBarTopCenterTimed(
            'This invoice billing line is $0 and cannot be adjusted',
            4000,
          );
          return;
        }

        // Set the taxable status of this invoiceline
        // If the invoiceLine.tax > 0, then it has tax and is taxable.
        this.isCurrentInvoiceLineTaxable = invoiceLine.tax > 0 ? true : false;

        // Prep all of the values needed for the selected invoice line
        const preppedInvoiceLineValues: IGBInvoiceAdjustmentDisplayData = this.prepInvoiceLineDisplay(invoiceLine);

        //this is dev only
        this.currentInvoiceTaxRate = preppedInvoiceLineValues.taxRate;

        // Set form values of prepped Invoice Billing Line
        this.setFormValuesForInvoiceBillingLine(preppedInvoiceLineValues);

        this.setNetGrossValidationLimits(preppedInvoiceLineValues);
      }
    } catch (err) {
      this.netAmountReadOnly = true;
      this.grossAmountReadOnly = true;
    }
  }

  setNoTaxAdjustmentValue(invoiceLine: InvoiceAdjustments): InvoiceAdjustments {
    let adjustmentAmount: number = 0;
    let ii: number = 0;

    for (ii = 0; ii < this.completedAdjustmentsMI.length; ii++) {
      if (this.completedAdjustmentsMI[ii].billingDetailId == invoiceLine.billingDetailID) {
        adjustmentAmount =
          adjustmentAmount +
          this.completedAdjustmentsMI[ii].adjustmentAmount +
          this.completedAdjustmentsMI[ii].taxAmount;
      }
    }

    invoiceLine.adjustment = adjustmentAmount;

    return invoiceLine;
  }

  prepInvoiceLineDisplay(invoiceLine: InvoiceAdjustments): IGBInvoiceAdjustmentDisplayData {
    let invoiceLineValues: IGBInvoiceAdjustmentDisplayData = {} as IGBInvoiceAdjustmentDisplayData;

    invoiceLineValues.grossAmount = invoiceLine.totalAmount + invoiceLine.adjustment;
    invoiceLineValues.taxRate = this.getTaxRateForIinvoiceLine(invoiceLine);

    invoiceLineValues.netAmount = invoiceLineValues.grossAmount / (1 + invoiceLineValues.taxRate);

    invoiceLineValues.taxAmount = this.getTaxAmountForInvoiceLine(
      invoiceLineValues.grossAmount,
      invoiceLineValues.taxRate,
    );

    return invoiceLineValues;
  }

  getTaxRateForIinvoiceLine(invoiceLine: InvoiceAdjustments): number {
    let taxRate: number;
    let ii: number;

    if (false == this.isCurrentInvoiceLineTaxable) {
      taxRate = 0;
    } else {
      taxRate = 0;
      for (ii = 0; ii < this.matchingTaxListForInvoice.length; ii++) {
        taxRate = taxRate + this.matchingTaxListForInvoice[ii].taxPercentage / 100;
      }
    }

    return taxRate;
  }

  setNetGrossValidationLimits(invoiceLineData: IGBInvoiceAdjustmentDisplayData): void {
    this.netAmountMinimumLimit =
      invoiceLineData.netAmount > 0 ? invoiceLineData.netAmount * -1 : invoiceLineData.netAmount;

    this.netAmountMaximumLimit =
      invoiceLineData.netAmount < 0 ? invoiceLineData.netAmount * -1 : invoiceLineData.netAmount;

    this.grossAmountMinimumLimit =
      invoiceLineData.grossAmount > 0 ? invoiceLineData.grossAmount * -1 : invoiceLineData.grossAmount;

    this.grossAmountMaximumLimit =
      invoiceLineData.grossAmount < 0 ? invoiceLineData.grossAmount * -1 : invoiceLineData.grossAmount;

    this.netAmountMinimumLimit = +this.#_decimalPipe
      .transform(this.netAmountMinimumLimit, '1.2-2')
      ?.toString()
      .replace(',', '');
    this.netAmountMaximumLimit = +this.#_decimalPipe
      .transform(this.netAmountMaximumLimit, '1.2-2')
      ?.toString()
      .replace(',', '');
    this.grossAmountMinimumLimit = +this.#_decimalPipe
      .transform(this.grossAmountMinimumLimit, '1.2-2')
      ?.toString()
      .replace(',', '');
    this.grossAmountMaximumLimit = +this.#_decimalPipe
      .transform(this.grossAmountMaximumLimit, '1.2-2')
      ?.toString()
      .replace(',', '');

    this.miInvoiceLineForm
      .get('netAmount')
      .setValidators([
        Validators.min(+this.#_decimalPipe.transform(this.netAmountMinimumLimit, '1.2-2')?.toString().replace(',', '')),
        Validators.max(+this.#_decimalPipe.transform(this.netAmountMaximumLimit, '1.2-2')?.toString().replace(',', '')),
        Validators.required,
      ]);
    this.miInvoiceLineForm.get('netAmount').updateValueAndValidity();

    this.miInvoiceLineForm
      .get('grossAmount')
      .setValidators([
        Validators.min(
          +this.#_decimalPipe.transform(this.grossAmountMinimumLimit, '1.2-2')?.toString().replace(',', ''),
        ),
        Validators.max(
          +this.#_decimalPipe.transform(this.grossAmountMaximumLimit, '1.2-2')?.toString().replace(',', ''),
        ),
        Validators.required,
      ]);

    this.miInvoiceLineForm.get('grossAmount').updateValueAndValidity();
  }

  getTaxAmountForInvoiceLine(grossAmount: number, taxRate: number): number {
    let taxAmount: number;

    if (false == this.isCurrentInvoiceLineTaxable) {
      taxAmount = 0;
    } else {
      taxAmount = grossAmount / (1 + taxRate);
    }

    return taxAmount;
  }

  setFormValuesForInvoiceBillingLine(preppedInvoiceLine: IGBInvoiceAdjustmentDisplayData): void {
    try {
      const netTestZeroBalance: number = +this.#_decimalPipe
        .transform(preppedInvoiceLine.grossAmount * -1, '1.2-2')
        ?.toString()
        .replace(',', '');

      if (netTestZeroBalance == 0) {
        this.netAmountReadOnly = true;
        this.grossAmountReadOnly = true;
      } else {
        this.netAmountReadOnly = false;
        this.grossAmountReadOnly = false;

        this.miInvoiceLineForm.patchValue({
          grossAmount: this.#_decimalPipe.transform(preppedInvoiceLine.grossAmount * -1, '1.2-2')?.toString(),
          netAmount: this.#_decimalPipe.transform(preppedInvoiceLine.netAmount * -1, '1.2-2')?.toString(),
        });
      }
    } catch (err) {
      this.netAmountReadOnly = true;
      this.grossAmountReadOnly = true;
    }
  }

  onChangeShowBillingLines(): void {
    this.showZeroBalanceInvoiceLines = !this.showZeroBalanceInvoiceLines;

    if (!this.showZeroBalanceInvoiceLines) {
      this.invoiceBillingLines = this.allinvoiceBillingLines;
    } else {
      this.invoiceBillingLines = [];
      let ii: number = 0;
      for (ii = 0; ii < this.allinvoiceBillingLines.length; ii++) {
        const lineItemTotal: number =
          this.allinvoiceBillingLines[ii].totalAmount + this.allinvoiceBillingLines[ii].adjustment;
        if (lineItemTotal != 0) {
          this.invoiceBillingLines.push(this.allinvoiceBillingLines[ii]);
        }
      }
    }

    this.resetInvoiceBillingLineData();
  }

  onAdjustmentCommentLostFocus(): void {
    const testEmptyString = this.miInvoiceLineForm.get('adjustmentComment').value.replace(' ', '');

    if (testEmptyString == '') {
      this.miInvoiceLineForm.patchValue({
        adjustmentComment: '',
      });
    }
  }

  onNetAmountLostFocus(): void {
    let netAmount: number = +this.miInvoiceLineForm.get('netAmount').value.replace(',', '');
    let netAmountAsString = this.miInvoiceLineForm.get('netAmount').value.replace(',', '');
    let grossAmount: number = 0;

    if (netAmountAsString == '') {
      this.miInvoiceLineForm.patchValue({
        grossAmount: '',
      });

      this.miInvoiceLineForm.patchValue({
        netAmount: '',
      });
    } else {
      if (netAmount > 0) {
        netAmount = netAmount * -1;
      }

      if (this.isCurrentInvoiceLineTaxable) {
        grossAmount = netAmount * (1 + this.currentInvoiceTaxRate);
      } else {
        grossAmount = netAmount;
      }

      this.miInvoiceLineForm.patchValue({
        grossAmount: this.#_decimalPipe.transform(grossAmount, '1.2-2')?.toString(),
      });

      this.miInvoiceLineForm.patchValue({
        netAmount: this.#_decimalPipe.transform(netAmount, '1.2-2')?.toString(),
      });
    }
  }

  onGrossAmountLostFocus(): void {
    let grossAmount: number = +this.miInvoiceLineForm.get('grossAmount').value.replace(',', '');
    let grossAmountAsString = this.miInvoiceLineForm.get('grossAmount').value.replace(',', '');
    let netAmount: number = 0;

    if (grossAmountAsString == '') {
      this.miInvoiceLineForm.patchValue({
        grossAmount: '',
      });

      this.miInvoiceLineForm.patchValue({
        netAmount: '',
      });
    } else {
      if (grossAmount > 0) {
        grossAmount = grossAmount * -1;
      }

      if (this.isCurrentInvoiceLineTaxable) {
        netAmount = grossAmount / (1 + this.currentInvoiceTaxRate);
      } else {
        netAmount = grossAmount;
      }

      this.miInvoiceLineForm.patchValue({
        netAmount: this.#_decimalPipe.transform(netAmount, '1.2-2')?.toString(),
        grossAmount: this.#_decimalPipe.transform(grossAmount, '1.2-2')?.toString(),
      });
    }
  }

  resetInvoiceBillingLineData(): void {
    this.miInvoiceLineForm.patchValue({
      netAmount: '',
      grossAmount: '',
      invoiceLineItem: '',
      adjustmentReason: '',
      adjustmentComment: '',
    });
  }

  //Get adjustment totals for a given invoice billing line
  getTotalsForInvoiceBillingLine(billingLineId: any): IInvoiceAdjustmentAmount {
    var adjustmentTotals: IInvoiceAdjustmentAmount = {} as IInvoiceAdjustmentAmount;
    var subtotal: number = 0;
    var tax: number = 0;
    var grandTotal: number = 0;
    var ii: number = 0;

    try {
      for (ii = 0; ii < this.completedAdjustmentsMI.length; ii++) {
        //NOTE: the generatebillinglineitemid could hold the line detail for a GB or MI
        if (this.completedAdjustmentsMI[ii].generateBillingLineItemId == billingLineId) {
          subtotal += this.completedAdjustmentsMI[ii].adjustmentAmount;
          tax += this.completedAdjustmentsMI[ii].taxAmount;
          grandTotal += this.completedAdjustmentsMI[ii].grossAdjustmentValue;
        }
      }

      adjustmentTotals.subtotal = subtotal;
      adjustmentTotals.tax = tax;
      adjustmentTotals.total = grandTotal;
    } catch (err) {}

    return adjustmentTotals;
  }

  dtaInvoiceBillingLines(): void {
    let ii: number = 0;

    for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
      const billingDetailLine: IMiAdjustmentBillingLineResponse =
        this.miInvoiceAdjustmentData.miBillingLinesResponse.find(
          (invoice) => invoice.billingDetailId == this.invoiceBillingLines[ii].billingDetailID,
        );
      // this.invoiceBillingLines[ii].primaryKey = billingDetailLine.billingDetailId.toLocaleString();
      this.invoiceBillingLines[ii].adjustment = billingDetailLine.adjustment;
      this.invoiceBillingLines[ii].tax = billingDetailLine.tax;
      this.invoiceBillingLines[ii].isTaxActive = billingDetailLine.tax > 0 ? true : false;
      this.invoiceBillingLines[ii].customerName = billingDetailLine.customerName;
    }
  }

  // HTTP Data

  getInvoiceBilingLinesForInvoiceNumber_MI(invoiceNumber: string) {
    this.#_invoiceService.getAdjustmentsForInvoiceNumber_MI(invoiceNumber).subscribe(
      (invoiceBillingLines) => {
        this.invoiceBillingLines = invoiceBillingLines;
      },
      (err: any) => {
        // let errorLogEntry: dbErrorEntry = new dbErrorEntry();
        // errorLogEntry.Location = 'Err: invoice-adjustments.ts::getInvoiceAdjustmentsForInvoiceNumber_GB().';
        // errorLogEntry.errorMessage = JSON.stringify(err);
        // this.apnUtilityService.logErrorToDB(errorLogEntry);
      },
      () => {
        //copy generateBillingLineItemID to primaryKey
        let ii: number = 0;
        for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
          this.invoiceBillingLines[ii].gbOriginalBillingDetailId = this.invoiceBillingLines[ii].billingDetailID;
        }

        ii = 0;
        for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
          this.invoiceBillingLines[ii].primaryKey = 'not used';

          //Add any adjustmnets to invoiceBillingLines array
          if (this.invoiceBillingLines[ii].adjustment < 0) {
            this.addedAdjustments.push({
              description: this.invoiceBillingLines[ii].customerName,
              adjustmentAmount: this.invoiceBillingLines[ii].adjustment,
            });
          }

          const adjustmentTotals: IInvoiceAdjustmentAmount = this.getTotalsForInvoiceBillingLine(
            this.invoiceBillingLines[ii].generateBillingLineItemId,
          );

          this.invoiceBillingLines[ii].calcedAdjustmentAmount = adjustmentTotals.total;

          //patch up invoieLine description
          const billingLines: IMiAdjustmentBillingLineResponse[] = this.miInvoiceAdjustmentData.miBillingLinesResponse;

          for (ii = 0; ii < this.invoiceBillingLines.length; ii++) {
            const miBillingLineResponse: IMiAdjustmentBillingLineResponse = billingLines.find(
              (line) => line.billingDetailId == this.invoiceBillingLines[ii].billingDetailID,
            );
            this.invoiceBillingLines[ii].description = miBillingLineResponse.description;
          }
        }

        this.dtaInvoiceBillingLines();

        this.miInvoiceLineForm.get('netAmount').setErrors(null);
        this.miInvoiceLineForm.get('grossAmount').setErrors(null);
        this.miInvoiceLineForm.get('adjustmentReason').setErrors(null);
        this.miInvoiceLineForm.get('adjustmentComment').setErrors(null);
        this.miInvoiceLineForm.updateValueAndValidity();

        //Keep this to remove/restore zero line invoice billing lines
        this.allinvoiceBillingLines = this.invoiceBillingLines;
      },
    );
  }

  getAdjustmentsCompletedForMI(invoiceNumber: string): void {
    this.#_invoiceService.getAdjustmentsCompleted_MI(invoiceNumber).subscribe(
      (adjustmentListResults) => {
        this.completedAdjustmentsMI = adjustmentListResults;
      },
      (err: any) => {
        // let errorLogEntry: dbErrorEntry = new dbErrorEntry();
        // errorLogEntry.Location = 'Err: invoice-adjustments.ts::getAdjustmentsCompletedForGB().';
        // errorLogEntry.errorMessage = JSON.stringify(err);
        // this.apnUtilityService.logErrorToDB(errorLogEntry);
      },
      () => {
        this.getMiInvoiceAdjustmentDetailsForInvoiceNumber(this.currentInvoiceNumber);
      },
    );
  }

  private getInvoiceAdjustmentReasonsForLotNumberFromDB(lotNumber: string): void {
    this.#_invoiceService.getAdjustmentReasonsForLotNumber(lotNumber).subscribe(
      (invoiceAdjustmentReasons) => {
        this.invoiceAdjustmentReasons = invoiceAdjustmentReasons;
      },
      (err: any) => {},
      () => {},
    );
  }

  getTaxListThatMatchInvoiceNumber(invoiceNumber: string): void {
    this.#_invoiceService.getTaxListThatMatchInvoiceDate(invoiceNumber).subscribe(
      (x) => {
        this.matchingTaxListForInvoice = x;
      },
      (err: any) => {},
      () => {
        let ii: number = 0;

        for (ii = 0; ii < this.matchingTaxListForInvoice.length; ii++) {
          let description: string = this.matchingTaxListForInvoice[ii].taxName;
          // remove old from tax type description
          description = description.replace(' - OLD', '');
          this.matchingTaxListForInvoice[ii].taxName = description;
        }
      },
    );
  }

  postIUAdjustment(
    invoiceAdjustmentData: AddInvoiceAdjustment,
    invoiceAdjustmentTax: AddInvoiceAdjustmentTax[],
    isTaxable: boolean,
  ): void {
    let newAdjustmentPostingId: number;
    let adjustmentData: InvoiceAdjustment = new InvoiceAdjustment();
    let adjustTaxes: AdjustTaxes = new AdjustTaxes();
    let migration: PostMigrateAdjustment = new PostMigrateAdjustment();
    let statusResult: boolean;

    adjustTaxes.adjustmentPostingId = 0; //This is just for initialization.  The actual new adjustmentPostingId is created and set in the API data methods
    adjustTaxes.invoiceNumber = invoiceAdjustmentData.InvoiceNumber;
    migration.invoiceNumber = invoiceAdjustmentData.InvoiceNumber;

    //Assign different adjustment objects to adjustmentData object
    adjustmentData.adjustmentPosting = invoiceAdjustmentData;
    adjustmentData.adjustmentPostingTax = invoiceAdjustmentTax;
    adjustmentData.adjustTaxes = adjustTaxes;
    adjustmentData.migrationAdjustment = migration;

    this.#_invoiceService.postInvoiceAdjustmentAllData(adjustmentData).subscribe(
      (postingAdjustmentResultStatus) => {
        statusResult = postingAdjustmentResultStatus;
      },
      (err: any) => {
        this.showWaitMessage = false;
        // let errorLogEntry: dbErrorEntry = new dbErrorEntry();

        // addExceptionMessage only logs to the console - <app-error-messages> is not placed in any
        // template, so the snackbar is the only feedback the user actually sees.
        this.#_messageDialogService.snackBarTopCenterTimed(
          'An error occurred and the adjustment failed to be recorded.',
          5000,
        );

        this.#_messageService.addExceptionMessage(
          'An error occurred while submitting this adjustment. Please review the adjustment data and resubmit. If the error continues to occur, please contact APN Support. Error Id #: ' +
            err.error,
        );
      },
      () => {
        this.showWaitMessage = false;

        this.getInvoiceAdjustmentReasonsForLotNumberFromDB(this.#_appGeneralState.currentLotNumber);
        this.getAdjustmentsCompletedForMI(this.currentInvoiceNumber);
        this.getInvoiceDetailBalancesForInvoiceNumber(this.currentInvoiceNumber);
        this.resetInvoiceBillingLineData();

        this.miInvoiceLineForm.get('netAmount').setErrors(null);
        this.miInvoiceLineForm.get('grossAmount').setErrors(null);
        this.miInvoiceLineForm.get('adjustmentReason').setErrors(null);
        this.miInvoiceLineForm.get('adjustmentComment').setErrors(null);
        this.miInvoiceLineForm.updateValueAndValidity();

        this.#_messageDialogService.snackBarTopCenterTimed('This adjustment have been successfully submitted.', 5000);
      },
    );
  }

  getInvoiceDetailBalancesForInvoiceNumber(invoiceNumber: string): void {
    let quickSearchResults: QuickSearchResults;

    this.#_invoiceService
      .getInvoiceDetailByInvoiceNumber(invoiceNumber, this.#_appGeneralState.currentLotNumber)
      .subscribe({
        error: (err: any) => {},
        next: (results) => (quickSearchResults = results),
        complete: () => {
          this.#_queryMessagegService.currentInvoiceBalance = quickSearchResults.currentBalance;
        },
      });
  }

  getMiInvoiceAdjustmentDetailsForInvoiceNumber(invoiceNumber: string): void {
    this.#_dataService.getMiInvoiceAdjustmentData(invoiceNumber).subscribe({
      error: (err: any) => {},
      next: (results) => (this.miInvoiceAdjustmentData = results),
      complete: () => {
        this.getInvoiceBilingLinesForInvoiceNumber_MI(this.currentInvoiceNumber);
      },
    });
  }

  // UTILITY METHODS

  getCurrentDate(): any {
    var today = new Date();
    var returnDate = '';

    var dd = today.getDate();
    var mm = today.getMonth() + 1;
    var yyyy = today.getFullYear();

    returnDate += `${mm}/${dd}/${yyyy}`;

    return returnDate;
  }
}
